/**
 * Create a _standalone_ source using altjs notation, i.e:
 * const AltSearchSource = {
 *   performSearch: {
 *     // remotely fetch something (required)
 *     remote(state) {
 *       return axios.get(`/search/q/${state.value}`);
 *     },
 *
 *     // this function checks in our local cache first
 *     // if the value is present it'll use that instead (optional).
 *     local(state) {
 *       return state.results[state.value] ? state.results : null;
 *     },
 *
 *     // here we setup some actions to handle our response
 *     loading: SearchActions.loadingResults, // (optional)
 *     success: SearchActions.receivedResults, // (required*)
 *     error: SearchActions.fetchingResultsFailed, // (required*)
 *
 *     // should fetch has precedence over the value returned by local in determining whether remote should be called
 *     // in this particular example if the value is present locally it would return but still fire off the remote request (optional)
 *     shouldFetch(state) {
 *       return true
 *     }
 *   }
 * };
 * [*] - required by alt. Optional for our createSource
 *
 * let SearchSource = createSource(SearchSearchSource);
 * SearchSource.performSearch({value: 42}).then(() => ...)
 *
 * For backwards compatibility alt definition is saved in Source.object
 * field and can be used in registerAsync
 *
 * @param  {Object} config Source defined like alt's Store expects it in
 *                         registerAsync method
 * @return {Object} source Source object
 */

/**
 * Figure out where the data should come from based on local and shouldFetch
 * methods, call respective actions on loading, resolution and error
 * @param  {Object}  method method object as a field value for Alt's
 *                          Source object
 * @param  {Array}   args   [description]
 * @return {Promise}        [description]
 */
function invokeMethod(method, args) {
  let shouldFetch = false;
  let localData = null;

  if (method.local != null) {
    localData = method.local(...args);
  }

  if (method.shouldFetch != null) {
    shouldFetch = method.shouldFetch(...args);
  } else {
    shouldFetch = localData == null;
  }

  let promise = null;
  if (shouldFetch) {
    if (method.loading != null) {
      setImmediate(method.loading);
    }

    promise = method.remote(...args).then(
      result => {
        if (method.success != null) {
          setImmediate(method.success, result);
        }
        return Promise.resolve(result);
      },
      error => {
        if (method.error != null) {
          setImmediate(method.error, error);
        }
        return Promise.reject(error);
      }
    );
  }

  if (localData != null) {
    if (method.success != null) {
      setImmediate(method.success, localData);
    }
    promise = Promise.resolve(localData);
  }

  return promise;
}

export default function createSource(config) {
  let source = {
    object: config, // Saved for backwards compatibility
  };

  for (let method in config) {
    if (config[method].remote == null) {
      throw new Error('remote method must be defined');
    }

    source[method] = (...args) => {
      return invokeMethod(config[method], args);
    };
  }

  return source;
}
